import { useCallback, useEffect, useRef, useState } from 'react';
export var RequestTimeoutState;
(function (RequestTimeoutState) {
    RequestTimeoutState["Idle"] = "idle";
    RequestTimeoutState["Loading"] = "loading";
    RequestTimeoutState["Slow"] = "slow";
    RequestTimeoutState["Timeout"] = "timeout";
    RequestTimeoutState["Success"] = "success";
    RequestTimeoutState["Error"] = "error";
})(RequestTimeoutState || (RequestTimeoutState = {}));
const DEFAULT_SLOW_THRESHOLD_MS = 3000;
const DEFAULT_TIMEOUT_THRESHOLD_MS = 80000;
export function useRequestTimeout(options) {
    const slowThresholdMs = options?.slowThresholdMs ?? DEFAULT_SLOW_THRESHOLD_MS;
    const timeoutThresholdMs = options?.timeoutThresholdMs ?? DEFAULT_TIMEOUT_THRESHOLD_MS;
    const [state, setState] = useState(RequestTimeoutState.Idle);
    const stateRef = useRef(RequestTimeoutState.Idle);
    const slowTimeoutRef = useRef(null);
    const timeoutRef = useRef(null);
    const trackingRef = useRef(false);
    useEffect(() => {
        stateRef.current = state;
    }, [state]);
    const clearTimers = useCallback(() => {
        if (slowTimeoutRef.current) {
            clearTimeout(slowTimeoutRef.current);
            slowTimeoutRef.current = null;
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);
    const reset = useCallback(() => {
        trackingRef.current = false;
        clearTimers();
        setState(RequestTimeoutState.Idle);
    }, [clearTimers]);
    const startTracking = useCallback(() => {
        trackingRef.current = true;
        clearTimers();
        setState(RequestTimeoutState.Loading);
        slowTimeoutRef.current = setTimeout(() => {
            if (trackingRef.current) {
                setState(RequestTimeoutState.Slow);
            }
        }, slowThresholdMs);
        timeoutRef.current = setTimeout(() => {
            if (trackingRef.current) {
                setState(RequestTimeoutState.Timeout);
            }
        }, timeoutThresholdMs);
    }, [clearTimers, slowThresholdMs, timeoutThresholdMs]);
    const stopTracking = useCallback((outcome) => {
        if (!trackingRef.current) {
            return;
        }
        trackingRef.current = false;
        clearTimers();
        if (stateRef.current === RequestTimeoutState.Timeout) {
            return;
        }
        setState(outcome);
    }, [clearTimers]);
    useEffect(() => {
        return () => {
            clearTimers();
        };
    }, [clearTimers]);
    return {
        state,
        startTracking,
        stopTracking,
        reset,
    };
}
